import { useMutation } from "@apollo/client";
import { ExclamationIcon } from "@heroicons/react/outline";
import { useState } from "react";

import { UseLabelType } from "@apps/use-label/use-label";
import { errorNotificationVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Dropdown from "@components/dropdown/dropdown";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import ToggleSwitch from "@components/toggle-switch/toggle-switch";
import useConfirm from "@helpers/hooks/use-confirm";

import deleteRoutineMutation from "../../graphql/delete-routine-mutation";
import routinesQuery from "../../graphql/routines-query";
import sendRoutineTestMutation from "../../graphql/send-routine-test-mutation";
import updateRoutineMutation from "../../graphql/update-routine-mutation";
import {
  parseTriggerConfig,
  routineTitle,
  triggerSummary,
} from "./routine-display";
import { SelectedMeetingGroup } from "./routine-meeting-group-multiselect";

export type RoutineCardRoutine = {
  id: string;
  name: string;
  skillId: string;
  skillSlug: string;
  enabled: boolean;
  triggerType: string;
  triggerConfig: string;
  skillExists: boolean;
  selectedMeetingGroups: SelectedMeetingGroup[];
};

type Props = {
  routine: RoutineCardRoutine;
  skillName?: string;
  label: UseLabelType;
  onEdit: () => void;
};

const RoutineCard: React.FC<Props> = ({
  routine,
  skillName,
  label,
  onEdit,
}) => {
  const [enabled, setEnabled] = useState(routine.enabled);
  const [sending, setSending] = useState(false);

  const { ConfirmationDialog, confirm } = useConfirm(
    "Delete routine?",
    "Are you sure you want to delete this routine?",
  );

  const [updateRoutine] = useMutation(updateRoutineMutation);
  const [deleteRoutine, { loading: deleting }] = useMutation(
    deleteRoutineMutation,
    { refetchQueries: [{ query: routinesQuery }] },
  );
  const [sendRoutineTest] = useMutation(sendRoutineTestMutation);

  const title = routineTitle(routine, skillName);
  const summary = triggerSummary(
    routine.triggerType,
    parseTriggerConfig(routine.triggerConfig),
    label,
  );

  const handleToggle = async (next: boolean) => {
    const previous = enabled;
    setEnabled(next);
    try {
      await updateRoutine({
        variables: { id: routine.id, enabled: next },
      });
    } catch (e) {
      setEnabled(previous);
      errorNotificationVar({
        title: "Could not update routine",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  const handleSendTest = async () => {
    setSending(true);
    try {
      await sendRoutineTest({ variables: { routineId: routine.id } });
      successNotificationVar({
        title: "Test sent",
        description:
          "This can take 30 seconds to a minute. Check your Slack or Teams.",
      });
    } catch (e) {
      errorNotificationVar({
        title: "Could not send test",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async () => {
    if (!(await confirm())) return;
    try {
      await deleteRoutine({ variables: { id: routine.id } });
      successNotificationVar({ title: "Routine deleted" });
    } catch (e) {
      errorNotificationVar({
        title: "Could not delete routine",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  const actionOptions: DropdownOptionType[] = [
    {
      label: "Send test",
      disabled: sending || !routine.skillExists,
      onClick: () => handleSendTest(),
    },
    { type: "divider" },
    {
      label: "Delete",
      textColor: "text-red-600",
      disabled: deleting,
      onClick: () => handleDelete(),
    },
  ];

  return (
    <div
      className="border rounded-md p-4 flex flex-col gap-3"
      aria-label="Routine card"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium text-gray-800 truncate">{title}</div>
          <div className="text-xs text-gray-500 mt-0.5 truncate">
            /{routine.skillSlug}
            {summary ? ` · ${summary}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ToggleSwitch
            checked={enabled}
            onChange={handleToggle}
            aria-label="Routine enable toggle"
          />
        </div>
      </div>

      {!routine.skillExists && (
        <div
          className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 rounded-md px-3 py-2"
          aria-label="Routine missing skill warning"
        >
          <ExclamationIcon className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            This skill no longer exists — edit or delete this routine.
          </span>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Button
          theme={buttonTheme.lightGray}
          size={ButtonSize.medium}
          text="Edit"
          aria-label="Edit routine"
          onClick={onEdit}
        />
        <Dropdown
          aria-label="Routine actions"
          align="end"
          size={ButtonSize.medium}
          options={actionOptions}
        />
      </div>
      <ConfirmationDialog />
    </div>
  );
};

export default RoutineCard;
